import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, ChapterTag } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

const JOURNEY_TYPES = ["Family", "Romantic", "Adventure", "Relaxation", "Luxury", "Cultural", "Other"];
const ARRANGEMENTS = [
  "Flights",
  "Hotel / Resort",
  "Local Transport",
  "Sightseeing Activities",
  "Dining",
  "Activities / Experiences",
  "Complete Trip Coordination",
];

const inputCls =
  "w-full rounded-sm border border-white/15 bg-white/[0.04] px-4 py-3.5 text-sm font-light text-zinc-100 placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-gold";
const labelCls = "mb-2 block text-[11px] font-medium uppercase tracking-[0.2em] text-zinc-400";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-xs font-medium tracking-wide transition-all duration-300 ${
      active ? "border-gold bg-gold text-obsidian" : "border-white/15 text-zinc-300 hover:border-gold/60 hover:text-gold"
    }`}
  >
    {children}
  </button>
);

const initialForm = {
  name: "",
  phone: "",
  email: "",
  destination: "",
  open_to_suggestions: false,
  start_date: "",
  end_date: "",
  adults: 2,
  children: 0,
  budget: "",
  journey_type: "",
  arrangements: [],
  notes: "",
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArrangement = (a) =>
    set("arrangements", form.arrangements.includes(a) ? form.arrangements.filter((x) => x !== a) : [...form.arrangements, a]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, phone number and email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", {
        ...form,
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
      });
      setDone(true);
      toast.success("Your enquiry has been sent.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="plan" data-testid="enquiry-section" className="grain relative bg-bone px-5 py-20 text-obsidian sm:px-8 sm:py-28 lg:px-16 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <ChapterTag index="10" label="Begin Here" light />
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-4">
            <h2 className="font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              Tell Us About <span className="italic text-gold-muted">Your Journey</span>
            </h2>
            <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-zinc-500 sm:text-base">
              Share your requirements with us. We'll review them and get back to you with a personalized proposal.
            </p>
            <div className="mt-10 hidden border-l-2 border-gold-muted/50 pl-6 lg:block">
              <p className="font-serif text-xl font-light italic leading-snug text-zinc-700">
                "Every Journey. Personally Managed."
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-8">
            {done ? (
              <div data-testid="enquiry-success" className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-sm border border-gold-muted/40 bg-white p-10 text-center shadow-xl">
                <CheckCircle2 size={44} strokeWidth={1.25} className="text-gold-muted" />
                <h3 className="mt-6 font-serif text-3xl font-light text-zinc-900">Thank you for choosing Nivaan Experiences.</h3>
                <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-zinc-500 sm:text-base">
                  We've received your journey requirements and will review them before getting back to you
                  with a personalized proposal.
                </p>
                <p className="mt-6 font-serif text-lg italic text-gold-muted">Every Journey. Personally Managed.</p>
                <button
                  data-testid="enquiry-again-button"
                  onClick={() => { setForm(initialForm); setDone(false); }}
                  className="mt-8 text-xs font-medium uppercase tracking-[0.25em] text-zinc-500 underline-offset-4 hover:text-gold-muted hover:underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form data-testid="enquiry-form" onSubmit={submit} className="rounded-sm border border-black/10 bg-white p-6 shadow-xl sm:p-10">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="f-name">Name *</label>
                    <input data-testid="field-name" id="f-name" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="f-phone">WhatsApp / Phone Number *</label>
                    <input data-testid="field-phone" id="f-phone" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." required />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="f-email">Email *</label>
                    <input data-testid="field-email" id="f-email" type="email" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" required />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="f-destination">Destination</label>
                    <input data-testid="field-destination" id="f-destination" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="e.g. Kashmir, Goa, Kerala..." disabled={form.open_to_suggestions} />
                    <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs font-light text-zinc-500">
                      <input data-testid="field-open-suggestions" type="checkbox" checked={form.open_to_suggestions} onChange={(e) => set("open_to_suggestions", e.target.checked)} className="h-4 w-4 accent-[#8C713A]" />
                      I'm open to suggestions
                    </label>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="f-start">Travel Start Date</label>
                    <input data-testid="field-start-date" id="f-start" type="date" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 focus:!border-gold-muted"} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="f-end">Travel End Date</label>
                    <input data-testid="field-end-date" id="f-end" type="date" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 focus:!border-gold-muted"} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="f-adults">Number of Adults</label>
                    <input data-testid="field-adults" id="f-adults" type="number" min="0" max="50" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 focus:!border-gold-muted"} value={form.adults} onChange={(e) => set("adults", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="f-children">Number of Children</label>
                    <input data-testid="field-children" id="f-children" type="number" min="0" max="50" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 focus:!border-gold-muted"} value={form.children} onChange={(e) => set("children", e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="f-budget">Approximate Budget</label>
                    <input data-testid="field-budget" id="f-budget" className={inputCls + " !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. ₹50,000 — whatever you're comfortable spending" />
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>Type of Journey</span>
                    <div className="flex flex-wrap gap-2">
                      {JOURNEY_TYPES.map((t) => (
                        <Chip key={t} testId={`journey-type-${t.toLowerCase()}`} active={form.journey_type === t} onClick={() => set("journey_type", form.journey_type === t ? "" : t)}>
                          {t}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>What would you like us to arrange?</span>
                    <div className="flex flex-wrap gap-2">
                      {ARRANGEMENTS.map((a) => (
                        <Chip key={a} testId={`arrange-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={form.arrangements.includes(a)} onClick={() => toggleArrangement(a)}>
                          {a}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="f-notes">Additional Preferences / Requirements</label>
                    <textarea data-testid="field-notes" id="f-notes" rows={4} className={inputCls + " resize-none !border-black/15 !bg-zinc-50 !text-zinc-900 placeholder-zinc-400 focus:!border-gold-muted"} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Tell us anything else you'd like us to know about your journey." />
                  </div>
                </div>
                <button
                  data-testid="enquiry-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-obsidian px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-zinc-900 disabled:opacity-60 sm:w-auto"
                >
                  {sending && <Loader2 size={16} className="animate-spin" />}
                  {sending ? "Sending..." : "Send My Enquiry"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
